import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';

interface Package {
  _id: string;
  name: string;
  type: 'developer' | 'broker' | 'owner';
  price: string;
  period: string;
  tagline: string;
  color: string;
  icon: string;
  popular: boolean;
  categories: { title: string; features: string[] }[];
}

export default function AdminPackages() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState<'developer' | 'broker' | 'owner'>('developer');

  const fetchPackages = async () => {
    try {
      if (!token) return;
      setLoading(true);
      const data = await api.adminGetPackages(token, activeType);
      setPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [token, activeType]);

  const handleDelete = async (id: string) => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await api.adminDeletePackage(token, id);
      toast.success('Package deleted');
      fetchPackages();
    } catch (err) {
      toast.error('Failed to delete package');
    }
  };

  const cleanText = (text: string) => text.replace(/[✅❌]/g, '').trim();
  const cleanTitle = (title: string) => cleanText(title.replace(/^\d+\.\s*/, '').replace(/\s*\(.*?\)/g, '').trim());

  return (
    <AdminLayout>
      <div className="p-6" style={{ fontFamily: 'Arial, sans-serif' }}>
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <p className="text-[13px] font-bold text-[#05144b] mb-1">Pricing System</p>
              <h1 className="text-3xl md:text-4xl font-bold text-[#05144b] leading-none">Manage Packages</h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Type Toggle */}
              <div className="flex bg-blue-50/50 p-1.5 rounded-xl border border-blue-900/10">
                {(['developer', 'broker', 'owner'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setActiveType(t)}
                    className={`px-5 py-2 rounded-lg text-[13px] font-bold transition-all ${
                      activeType === t
                        ? 'bg-white border-2 border-[#05144b] text-[#05144b] shadow-sm'
                        : 'text-blue-900/40 hover:text-[#05144b]'
                    }`}
                  >
                    {t === 'developer' ? 'Developers' : t === 'broker' ? 'Brokers' : 'Owners'}
                  </button>
                ))}
              </div>

              {/* Add Package Button - small */}
              <button
                onClick={() => navigate('/admin-add-package')}
                className="flex items-center gap-2 px-6 py-3 rounded-lg bg-white border-2 border-[#05144b] text-[#05144b] font-bold text-[14px] hover:bg-blue-50 transition-all active:scale-95 shadow-md"
              >
                <i className="fa-solid fa-plus text-sm"></i>
                Add Package
              </button>
            </div>
          </div>

          {/* Package Cards */}
          {loading ? (
            <div className="p-16 text-center font-bold text-[#05144b] animate-pulse text-sm">
              Fetching {activeType} packages...
            </div>
          ) : packages.length === 0 ? (
            <div className="p-16 text-center">
              <i className="fa-solid fa-box-open text-4xl text-gray-300 mb-4 block"></i>
              <p className="font-bold text-[#05144b]/40 text-xs">No {activeType} packages found</p>
              <p className="text-[#05144b]/40 mt-1 text-sm">Click "Add Package" to create one.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {packages.map((pkg) => (
                <div
                  key={pkg._id}
                  className="p-6 rounded-2xl border border-gray-200 bg-gray-50 hover:border-gray-400 transition-all group"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-lg mb-4"
                    style={{ backgroundColor: `${pkg.color}15`, color: pkg.color }}
                  >
                    <i className={`fa-solid ${pkg.icon}`}></i>
                  </div>
                   <h3 className="text-[18px] font-bold text-[#05144b] mb-1">{cleanText(pkg.name)}</h3>
                  <div className="flex items-baseline gap-1 mb-3">
                     <span className="text-xl font-bold text-[#05144b]">Rs.{pkg.price}</span>
                    <span className="text-[12px] font-bold text-[#05144b]/40">/ {pkg.period}</span>
                  </div>
                   <p className="text-xs text-[#05144b] font-medium mb-6 line-clamp-2 min-h-[2rem]">{cleanText(pkg.tagline)}</p>

                  <div className="space-y-1.5 mb-6">
                    {pkg.categories.slice(0, 3).map((cat, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-[#05144b]"></div>
                         <span className="text-[10px] font-bold text-[#05144b] tracking-wider">{cleanTitle(cat.title)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin-edit-package/${pkg._id}`)}
                      className="flex-1 py-2.5 rounded-lg bg-white border-2 border-[#05144b] text-[#05144b] text-[13px] font-bold hover:bg-blue-50 transition-all active:scale-95"
                    >
                      Edit
                    </button>
                      <button
                       onClick={() => handleDelete(pkg._id)}
                       className="py-2.5 px-3 rounded-lg bg-white text-[#05144b] text-[10px] font-bold border border-blue-900/10 hover:bg-rose-600 hover:text-white hover:border-rose-600 transition-all active:scale-95 shadow-sm"
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}


